import { isRecord } from '@apps/web/components/phoebe_chat/record_utils';
import { type StreamRunState } from '@apps/web/components/phoebe_chat/streaming_types';
import {
    type RunTraceEntry,
    type StructuredReportArtifactEntry,
    type ToolExecutionEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';

// Pulls structured-report-shaped tool outputs out of the run trace so
// they can be rendered as attachments after the assistant text rather
// than as a tool-call card before it.
export type ParsedStructuredReport = {
    readonly columns: ReadonlyArray<string>;
    readonly rows: ReadonlyArray<ReadonlyArray<string>>;
    readonly summary: {
        readonly returned: number;
        readonly truncated: boolean;
        readonly filters: Record<string, unknown>;
    };
};

export function parseStructuredReportFromOutput(
    output: unknown,
): ParsedStructuredReport | null {
    if (!isRecord(output)) {
        return null;
    }
    const rawColumns = output['columns'];
    const rawRows = output['rows'];
    if (!Array.isArray(rawColumns) || !Array.isArray(rawRows)) {
        return null;
    }
    const columns = rawColumns.filter(
        (c): c is string => typeof c === 'string',
    );
    if (columns.length === 0) {
        return null;
    }
    const rows = rawRows
        .filter((row): row is ReadonlyArray<unknown> => Array.isArray(row))
        .map((row) =>
            row.map((cell) =>
                cell === null || cell === undefined ? '' : String(cell),
            ),
        );
    const rawSummary = isRecord(output['summary']) ? output['summary'] : {};
    return {
        columns,
        rows,
        summary: {
            returned:
                typeof rawSummary['returned'] === 'number'
                    ? rawSummary['returned']
                    : rows.length,
            truncated: rawSummary['truncated'] === true,
            filters: isRecord(rawSummary['filters'])
                ? rawSummary['filters']
                : {},
        },
    };
}

export function collectStructuredReportArtifacts(
    entries: Array<RunTraceEntry>,
): Array<StructuredReportArtifactEntry> {
    const toolEntries: Array<ToolExecutionEntry> = [];
    for (const entry of entries) {
        if (entry.kind === 'tool-execution') {
            toolEntries.push(entry);
            continue;
        }
        if (entry.kind === 'thought') {
            toolEntries.push(...entry.tools);
        }
    }

    return toolEntries.flatMap((toolEntry) => {
        if (!toolEntry.isComplete) {
            return [];
        }
        const parsed = parseStructuredReportFromOutput(toolEntry.output);
        if (!parsed) {
            return [];
        }
        return [
            {
                kind: 'structured-report-artifact',
                id: `structured-report-artifact-${toolEntry.id}`,
                createdAt: toolEntry.createdAt,
                toolName: toolEntry.name,
                toolEntryId: toolEntry.id,
                columns: parsed.columns,
                rows: parsed.rows,
                summary: parsed.summary,
                turnSequence: toolEntry.turnSequence,
            },
        ];
    });
}

export function collectStructuredReportArtifactsFromRun(
    run: StreamRunState | null,
): Array<StructuredReportArtifactEntry> {
    if (!run) {
        return [];
    }
    return run.orderedNodeIds.flatMap((nodeId) => {
        const node = run.nodesById[nodeId];
        if (!node || node.kind !== 'tool' || !node.isComplete) {
            return [];
        }
        const parsed = parseStructuredReportFromOutput(node.result);
        if (!parsed) {
            return [];
        }
        return [
            {
                kind: 'structured-report-artifact',
                id: `live-structured-report-artifact-${node.id}`,
                createdAt: node.createdAt,
                toolName: node.toolName ?? '',
                toolEntryId: node.id,
                columns: parsed.columns,
                rows: parsed.rows,
                summary: parsed.summary,
            },
        ];
    });
}
